import cloneDeep from "lodash/cloneDeep";
import {
    MAX_SPIRAL_SECTIONS,
    Operations,
    ProblemData,
    ProblemFormatOptions,
    SPIRAL_SECTION_FORMATS,
} from "./Constants.ts";
import { ISettingsProps } from "./Models.ts";

/**
 * Spiral review: three or four labelled sections stacked down one page, each of
 * one format, each generated by that format's existing generator (ADR 0013).
 *
 * Not a shuffled grid of mixed problems - `Page.tsx` sizes a row by its tallest
 * member, so a row mixing a clock face and an addition fact leaves the fact
 * floating in a tall box. Sections keep each format's density a local question
 * that is already answered.
 */

/** The heading printed above a section. */
function headingFor(format: ProblemFormatOptions): string {
    switch (format) {
        case ProblemFormatOptions.MathFacts:
            return "Math facts";
        case ProblemFormatOptions.Fractions:
            return "Fractions";
        case ProblemFormatOptions.Money:
            return "Money";
        case ProblemFormatOptions.Decimals:
            return "Decimals";
        case ProblemFormatOptions.PlaceValue:
            return "Place value";
        case ProblemFormatOptions.RoundingComparing:
            return "Rounding & comparing";
        case ProblemFormatOptions.TellingTime:
            return "Telling time";
        default:
            return String(format);
    }
}

/** The settings a section is generated at: the sheet's settings, that format. */
function sectionSettings(base: ISettingsProps, format: ProblemFormatOptions, count: number): ISettingsProps {
    const settings = cloneDeep(base);
    settings.Format = format;
    settings.NumberOfProblemsPerPage = count;
    settings.NumberOfPages = 1;
    settings.HalfPage = false;
    settings.AnswerPages = false;
    settings.DifferentiationSet = false;
    // A spiral's math-facts section is a few facts among other formats, not a
    // level test, and the level control is not shown here (ADR 0071 §3).
    settings.FactLevel = 0;
    // Math facts sections use addition and subtraction, never long division
    // (its bracket layout is Math-Facts-only and reads `numbers` directly).
    if (format === ProblemFormatOptions.MathFacts) {
        settings.Operators = settings.Operators.filter(
            (operator) => operator.value !== Operations.LongDivision
        );
        if (settings.Operators.length === 0) {
            settings.Operators = base.Operators.filter(
                (operator) => operator.value === Operations.Addition
            );
        }
        settings.MissingNumber = base.MissingNumber;
    }
    return settings;
}

/** Only the section formats a spiral sheet allows, telling time capped at one. */
export function usableSections(
    sections: { format: ProblemFormatOptions; count: number }[]
): { format: ProblemFormatOptions; count: number }[] {
    const out: { format: ProblemFormatOptions; count: number }[] = [];
    let timeUsed = false;
    for (const section of sections) {
        if (!SPIRAL_SECTION_FORMATS.includes(section.format)) continue;
        if (section.count <= 0) continue;
        if (section.format === ProblemFormatOptions.TellingTime) {
            if (timeUsed) continue;
            timeUsed = true;
        }
        out.push(section);
        if (out.length >= MAX_SPIRAL_SECTIONS) break;
    }
    return out;
}

/**
 * A page-shaped set of spiral-review problems: one page, its problems tagged
 * with the section they belong to. `generateSection` is the ordinary generator
 * (GenerateDataV2), passed in to avoid a circular import.
 */
export function generateSpiralReviewPages(
    settings: ISettingsProps,
    generateSection: (settings: ISettingsProps) => ProblemData[][] | null
): ProblemData[][] | null {
    const sections = usableSections(settings.SpiralSections ?? []);
    if (sections.length === 0) {
        return null;
    }

    const problems: ProblemData[] = [];
    for (const section of sections) {
        const heading = headingFor(section.format);
        const pages = generateSection(sectionSettings(settings, section.format, section.count));
        const sectionProblems = pages?.[0] ?? [];
        for (const problem of sectionProblems) {
            // The heading doubles as the group key (ADR 0040 §1) - this is the
            // grouping spiral review always had, carried over unchanged: two
            // sections of the same format still merge into one, which is a
            // pre-existing property of grouping by heading, not a new one.
            problems.push({ ...problem, problemGroup: { key: heading, heading, format: section.format } });
        }
    }

    return problems.length > 0 ? [problems] : null;
}
